/**
 * TransactionFeedPage
 * Filterable list of bank transactions with match status badges and actions
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import type { BankTransaction } from '../api/external-payments.api';
import { fetchTransactions, fetchOnboardingData } from '../api/external-payments.api';
import { TransactionRow } from '../components/TransactionRow';
import type { TenantOption } from '../components/TransactionRow';
import { PageHeader, EmptyState } from '../components/layout/index';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

type MatchStatusFilter =
  | 'all'
  | 'unmatched'
  | 'suggested'
  | 'confirmed'
  | 'auto_matched'
  | 'dismissed';

type DirectionFilter = 'all' | 'incoming' | 'outgoing';

const STATUS_FILTER_OPTIONS: { value: MatchStatusFilter; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'unmatched', label: 'Unmatched' },
  { value: 'suggested', label: 'Suggested' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'auto_matched', label: 'Auto Matched' },
  { value: 'dismissed', label: 'Dismissed' },
];

export function TransactionFeedPage(): JSX.Element {
  const { propertyId } = useParams<{ propertyId: string }>();

  const [transactions, setTransactions] = useState<BankTransaction[]>([]);
  const [tenantOptions, setTenantOptions] = useState<TenantOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<MatchStatusFilter>('all');
  const [directionFilter, setDirectionFilter] = useState<DirectionFilter>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadTransactions = useCallback(async () => {
    if (!propertyId) return;
    try {
      setIsLoading(true);
      setError(null);
      const [data, onboarding] = await Promise.all([
        fetchTransactions(propertyId, {
          matchStatus: statusFilter !== 'all' ? statusFilter : undefined,
          direction: directionFilter !== 'all' ? directionFilter : undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
        }),
        fetchOnboardingData(propertyId).catch(() => ({ tenants: [] })),
      ]);
      setTransactions(data.transactions);
      setTenantOptions(
        onboarding.tenants.map((t) => ({
          tenantId: t.tenantId,
          leaseId: t.leaseId,
          firstName: t.firstName,
          lastName: t.lastName,
          unitNumber: t.unitNumber,
          address1: t.address1,
        }))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load transactions');
    } finally {
      setIsLoading(false);
    }
  }, [propertyId, statusFilter, directionFilter, dateFrom, dateTo]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  if (isLoading) {
    return (
      <div data-testid="transaction-feed-page" className="max-w-4xl mx-auto pb-6">
        <div className="px-[var(--page-padding-x)]">
          <div className="mb-6">
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-80" />
          </div>
          <div className="rounded-lg border border-border bg-card p-4 mb-6">
            <div className="flex flex-wrap gap-3">
              <Skeleton className="h-9 w-32" />
              <Skeleton className="h-9 w-28" />
              <Skeleton className="h-9 w-36" />
              <Skeleton className="h-9 w-36" />
            </div>
          </div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-20 w-full rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="transaction-feed-page"
      className="max-w-4xl mx-auto pb-6"
    >
      <div className="px-[var(--page-padding-x)]">
        <PageHeader
          title="Transaction Feed"
          subtitle="Review and manage incoming bank transactions for rent matching."
        />

        {/* Filter bar */}
        <div className="rounded-lg border border-border bg-card p-4 mb-6">
          <div className="flex flex-wrap gap-3">
            <div>
              <label htmlFor="transaction-filter-status" className="block text-xs font-medium text-muted-foreground mb-1">
                Status
              </label>
              <select
                id="transaction-filter-status"
                data-testid="transaction-filter-status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as MatchStatusFilter)}
                className="px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {STATUS_FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="direction-filter" className="block text-xs font-medium text-muted-foreground mb-1">
                Direction
              </label>
              <select
                id="direction-filter"
                value={directionFilter}
                onChange={(e) => setDirectionFilter(e.target.value as DirectionFilter)}
                className="px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="all">All</option>
                <option value="incoming">Incoming</option>
                <option value="outgoing">Outgoing</option>
              </select>
            </div>

            <div>
              <label htmlFor="date-from-input" className="block text-xs font-medium text-muted-foreground mb-1">
                From
              </label>
              <input
                id="date-from-input"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div>
              <label htmlFor="date-to-input" className="block text-xs font-medium text-muted-foreground mb-1">
                To
              </label>
              <input
                id="date-to-input"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {(statusFilter !== 'all' || directionFilter !== 'all' || dateFrom || dateTo) && (
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setStatusFilter('all');
                    setDirectionFilter('all');
                    setDateFrom('');
                    setDateTo('');
                  }}
                >
                  Clear filters
                </Button>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="flex justify-between items-center px-4 py-3 bg-destructive/5 border border-destructive/20 rounded-lg mb-6">
            <p className="m-0 text-sm text-destructive">{error}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-destructive"
              onClick={loadTransactions}
            >
              Retry
            </Button>
          </div>
        )}

        {transactions.length === 0 ? (
          <EmptyState
            title={
              statusFilter !== 'all' || directionFilter !== 'all' || dateFrom || dateTo
                ? 'No transactions match your filters.'
                : 'No transactions found.'
            }
            description={
              statusFilter !== 'all' || directionFilter !== 'all' || dateFrom || dateTo
                ? undefined
                : 'Make sure a bank account is linked for this property.'
            }
          />
        ) : (
          <div data-testid="transaction-list">
            {transactions.map((tx) => (
              <TransactionRow key={tx.id} transaction={tx} tenantOptions={tenantOptions} onUpdated={loadTransactions} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default TransactionFeedPage;
